import { formatEther, parseEther } from 'ethers'
import * as React from 'react'

export type BigNumberInputProps = {
  onChange: (value: bigint) => void
  bnValue: bigint
  placeholder?: string
  max?: bigint
  min?: bigint
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'min' | 'max' | 'value' | 'onChange'>

export function BigNumberInput({
  bnValue,
  max,
  min,
  onChange,
  placeholder = '0.00',
  ...props
}: BigNumberInputProps) {
  const [inputValue, setInputValue] = React.useState('')
  const [isEditing, setIsEditing] = React.useState(false)

  // Synchronize inputValue with bnValue when not editing
  React.useEffect(() => {
    if (!isEditing) {
      setInputValue(bnValue === 0n ? '' : formatEther(bnValue))
    }
  }, [bnValue, isEditing])

  const updateValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget

    if (!/^\d*\.?\d*$/.test(value)) {
      return
    }

    setInputValue(value)
    setIsEditing(true)

    if (value === '') {
      onChange(0n)
      return
    }

    let bigNumValue: bigint
    try {
      bigNumValue = parseEther(value)
    } catch (e) {
      // Invalid input, don't trigger onChange
      return
    }

    const invalidValue = (min && bigNumValue < min) || (max && bigNumValue > max)
    if (!invalidValue) {
      onChange(bigNumValue)
    }
  }

  const handleBlur = () => {
    // Format the value on blur
    setIsEditing(false)
    if (inputValue === '') {
      setInputValue('')
      onChange(0n)
    } else {
      try {
        const formattedValue = formatEther(parseEther(inputValue))
        setInputValue(formattedValue)
      } catch {
        // Ignore invalid input
      }
    }
  }

  const inputProps = {
    placeholder,
    onChange: updateValue,
    onBlur: handleBlur,
    type: 'text',
    value: inputValue,
  }

  return <input {...inputProps} {...props} />
}
